import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import Radom, { Customer, Deposit, DepositAccount, ISubscription } from '../../state/Radom'
import Spinner from '../../components/Spinner'
import { Container } from '../../components/Animations'
import BreadCrumbs from '../../components/Breadcrumbs'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Dots from '../../icons/Dots'
import User from '../../state/User'
import styled from 'styled-components'
import { RADOM_COLORS } from '../../util/Constants'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import Edit from '../../icons/Edit'
import EditTagsModal from './EditTags'
import { errorToast, useTestMode } from '../../util/Util'
import { prodNetworks, testNetworks } from '../../util/Managed'
import { StatusBadge } from '../CheckoutSessions'
import Rotate from '../../icons/Rotate'
import { genSectionItems, MDASH } from '../../util/SectionItems'
import { Section, SubTitle, SectionContent, SectionItem } from '../../components/Shared'
import { CustomerTabs, depositMock } from './CustomerTypes'
import CustomerTransactions from './CustomerTransactions'
import AnimatedTabs from '../../components/AnimatedTabs'
import CustomerDepositAccounts from './CustomerDepositAccounts'
import CustomerPayoutsAccounts from './CustomerPayoutsAccounts'
import CreateExternalAccountModal from './CreateExternalAccountModal'

// ---------------------------------------------------------------------------------------------------------------------
export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
`

export const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background-color: ${RADOM_COLORS.GRAY9};
  border-radius: 5px;
  padding: 5px 10px;
`

// ---------------------------------------------------------------------------------------------------------------------
const CustomerView = observer((): ReactElement => {
  const params = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [customer, setCustomer] = useState<Customer>()
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([])
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [activeTab, setActiveTab] = useState(CustomerTabs.Transactions)
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false)
  const [isCreatePayoutAccountModalOpen, setIsCreatePayoutAccountModalOpen] = useState(false)
  const payoutAccountsRefreshRef = useRef<(() => void) | null>(null)
  const [testMode] = useTestMode()
  const [idCardRotationAngle, setIdCardRotationAngle] = useState(0)
  const [selfieRotationAngle, setSelfieRotationAngle] = useState(0)

  const loadCustomer = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomer(params.id, testMode ? testNetworks : prodNetworks)
      .then(s => setCustomer(s))
      .catch(e => console.error('Failed to look up checkout session', e))
      .finally(() => setIsLoading(false))
  }

  const fetchSubscriptions = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerSubscriptions(params.id)
      .then(subs => {
        setSubscriptions(subs)
      })
      .catch(e => console.error('Failed to get this customer\'s subscriptions', e))
      .finally(() => setIsLoading(false))
  }

  const fetchDeposits = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerDeposits(params.id)
      .then(deposits => {
        setDeposits(deposits)
      })
      .catch(e => console.error('Failed to get this customer\'s deposits', e))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => { loadCustomer() }, [testMode])
  useEffect(() => {
    fetchSubscriptions()
    fetchDeposits()
  }, [customer])
  const customerColumnCount = 2
  const subscriptionColumnCount = 3
  const depositColumnCount = 3

  const getActiveTabContent = (): JSX.Element => {
    switch (activeTab) {
    case CustomerTabs.Transactions:
      return <CustomerTransactions
        customer={customer}
        subscriptions={subscriptions}
        subscriptionColumnCount={subscriptionColumnCount}
        deposits={deposits}
        depositColumnCount={depositColumnCount}
      />
    case CustomerTabs.DepositAccounts:
      return <CustomerDepositAccounts
        depositAccounts={depositMock as DepositAccount[]}
        isLoading={isLoading}
        customerId={customer?.id ?? '' }
      />
    case CustomerTabs.PayoutAccounts:
      return <CustomerPayoutsAccounts
        customerId={customer?.id ?? ''}
        isLoading={isLoading}
        onRefreshRef={payoutAccountsRefreshRef}
      />
    default:
      return <></>
    }
  }

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!customer) {
    return <></>
  }
  const isBusiness = customer.accountType === 'Business'

  // -------------------------------------------------------------------------------------------------------------------
  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

      <BreadCrumbs items={[
        { item: 'Customers', to: '/customers' },
        { item: customer.id, to: '.' }
      ]} />

      {
        false &&
        <Dropdown
          noChevron
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <DropdownItem onClick={() => { }}>Something</DropdownItem>
            </div>
          }
        />
      }

    </div>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer details
      // ---------------------------------------------------------------------------------------------------------------
    }
    <Section>
      <SubTitle style={{ fontSize: 18 }}>
        Customer details
      </SubTitle>

      <SectionContent numColumns={customerColumnCount}>
        {
          isBusiness &&
          genSectionItems([
            { heading: 'Company Name', content: customer.companyName },
            { heading: 'Company Number', content: customer.companyNumber },
            { heading: '', content: null }
          ], customerColumnCount)
        }

        <SectionItem>
          <SubTitle>ID</SubTitle>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 10,
            alignItems: 'center'
          }}>
            {customer.id}
            {
              customer.tags.length > 0 &&
              customer.tags.includes('identity_verified') &&
              <StatusBadge style={{ marginLeft: 10 }} status="active">
                KYC Verified
              </StatusBadge>
            }
          </div>
        </SectionItem>

        {
          genSectionItems([
            { heading: 'Name', content: customer.name },
            { heading: 'Email Address', content: customer.email },
            { heading: 'Address', content: customer.billingAddress },
            { heading: 'Date of Birth', content: customer.dob },
            { heading: 'Country of Residence', content: customer.residenceCountry }
          ], customerColumnCount)
        }

        {
          User.organization &&
          <SectionItem>
            <SubTitle>Total spend</SubTitle>
            <div>
              {
                Number(customer.totalSpend) <= 0
                  ? MDASH
                  : new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: User.organization?.defaultCurrency
                  }).format(Number(customer.totalSpend))
              }
            </div>
          </SectionItem>
        }

        <SectionItem>
          <SubTitle>
            <span>Tags</span>
            <SecondaryButton onClick={() => setIsTagsModalOpen(true)} style={{ padding: 0, border: 0, fontSize: 0 }}>
              <Edit stroke={RADOM_COLORS.GRAY_DARKER} style={{ height: 15 }} />
            </SecondaryButton>
          </SubTitle>
          <TagsContainer>
            {customer.tags.length <= 0 && MDASH}
            {customer.tags.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </TagsContainer>

        </SectionItem>

        {
          genSectionItems([
            { heading: 'Phone', content: customer.phone },
            { heading: 'Telegram', content: customer.telegram },
            { heading: 'Discord', content: customer.discord }
          ], customerColumnCount)
        }
      </SectionContent>
    </Section>

    <AnimatedTabs
      selectedTab={activeTab}
      tabs={Object.values(CustomerTabs)}
      onTabChange={(tab) => setActiveTab(tab as CustomerTabs)}
      rightContent={activeTab === CustomerTabs.PayoutAccounts
        ? <PrimaryButton
          style={{ fontSize: 13, padding: '6px 16px', whiteSpace: 'nowrap' }}
          onClick={() => setIsCreatePayoutAccountModalOpen(true)}
        >
            Create payout account
        </PrimaryButton>
        : undefined
      }
    />

    <CreateExternalAccountModal
      customerId={customer?.id ?? ''}
      title='Create payout account'
      visible={isCreatePayoutAccountModalOpen}
      onClose={() => setIsCreatePayoutAccountModalOpen(false)}
      onAccountCreated={() => {
        setIsCreatePayoutAccountModalOpen(false)
        payoutAccountsRefreshRef.current?.()
      }}
    />

    {getActiveTabContent()}
    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer KYC info
      // ---------------------------------------------------------------------------------------------------------------
      customer.tags.length > 0 &&
      customer.tags.includes('identity_verified') &&
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Verfications
        </SubTitle>
        <SectionContent
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between', // 添加这一行
            gap: 20,
            width: '100%' // 建议加上宽度控制
          }}
        >

          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Passport/IDcard
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setIdCardRotationAngle(idCardRotationAngle + 90)
                  console.log('idCardRotationAngle', idCardRotationAngle)
                }}
              />
            </SubTitle>
            <div style={{ padding: 10, overflow: 'hidden' }}>
              {customer.kycData?.idCardUrl || customer.kycData?.passportUrl
                ? <img
                  src={customer.kycData?.idCardUrl || customer.kycData?.passportUrl}
                  onClick={() =>
                    window.open(customer.kycData?.idCardUrl || customer.kycData?.passportUrl, '_blank')
                  }
                  style={{
                    transform: `rotate(${idCardRotationAngle}deg)`,
                    maxHeight: 300,
                    height: 'auto',
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: 10
                  }}
                />
                : null}
            </div>
          </SectionItem>
          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Selfie
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setSelfieRotationAngle(selfieRotationAngle + 90)
                  console.log('selfieRotationAngle', selfieRotationAngle)
                }}
              />
            </SubTitle>
            <div
              style={{
                width: 'auto',
                maxHeight: 300,
                overflow: 'hidden',
                padding: 10
              }}
            >
              <img
                src={customer.kycData?.selfieUrl}
                onClick={() => window.open(customer.kycData?.selfieUrl, '_blank')}
                style={{
                  transform: `rotate(${selfieRotationAngle}deg)`,
                  maxHeight: 300,
                  height: 'auto',
                  width: '100%',
                  objectFit: 'contain',
                  borderRadius: 10
                }}
              />
            </div>
          </SectionItem>
        </SectionContent>
      </Section>
    }

    {
      isTagsModalOpen &&
      <EditTagsModal
        onClose={() => setIsTagsModalOpen(false)}
        isOpen={isTagsModalOpen}
        tags={customer.tags}
        onSave={async tags => {
          try {
            await Radom.updateCustomer(customer.id, { tags })
            setIsTagsModalOpen(false)
            loadCustomer()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }} />
    }
  </Container>
})

export default CustomerView
